import sodium from 'libsodium-wrappers-sumo';
import type { RecordKind, RecordPayload } from './model';

const encoder = new TextEncoder();
const decoder = new TextDecoder();
const RECORD_WRAP_CONTEXT = 'safe-wallet:record-key:v1';
const RECORD_PAYLOAD_CONTEXT = 'safe-wallet:record-payload:v1';
const DEVICE_WRAP_CONTEXT = 'safe-wallet:device-vrk:v1';
const DEVICE_FINGERPRINT_CONTEXT = 'safe-wallet:device-pairing-key-fingerprint:v1';
const DEVICE_FINGERPRINT_BYTES = 10;
const ACCOUNT_WALLET_WRAP_CONTEXT = 'safe-wallet:account-wallet-key:v1';
const HELLO_DEVICE_SECRET_WRAP_CONTEXT = 'safe-wallet:hello-device-secret:v1';

function webCryptoBytes(value: Uint8Array): Uint8Array<ArrayBuffer> {
  const copy = new Uint8Array(value.byteLength);
  copy.set(value);
  return copy;
}

export interface CipherPacket {
  nonce: string;
  ciphertext: string;
}

export interface EncryptedRevision {
  cryptoVersion: 1;
  wrappedRecordKey: string;
  wrappedRecordKeyNonce: string;
  ciphertext: string;
  nonce: string;
}

export interface PinEnvelope {
  version: 1;
  kdf: {
    name: 'argon2id';
    salt: string;
    opsLimit: number;
    memLimit: number;
  };
  wrappedVaultRootKey: CipherPacket;
}

export interface AccountWalletKeyPackage {
  version: 1;
  kdf: {
    name: 'argon2id';
    salt: string;
    opsLimit: number;
    memLimit: number;
  };
  wrappedVaultRootKey: CipherPacket;
}

function toBase64(bytes: Uint8Array): string {
  return sodium.to_base64(bytes, sodium.base64_variants.URLSAFE_NO_PADDING);
}

function fromBase64(value: string): Uint8Array {
  return sodium.from_base64(value, sodium.base64_variants.URLSAFE_NO_PADDING);
}

function associatedData(parts: string[]): Uint8Array {
  return encoder.encode(parts.join('|'));
}

function encryptPacket(plaintext: Uint8Array, key: Uint8Array, aad: Uint8Array): CipherPacket {
  const nonce = sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES);
  const ciphertext = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
    plaintext,
    aad,
    null,
    nonce,
    key,
  );
  return { nonce: toBase64(nonce), ciphertext: toBase64(ciphertext) };
}

function decryptPacket(packet: CipherPacket, key: Uint8Array, aad: Uint8Array): Uint8Array {
  return sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
    null,
    fromBase64(packet.ciphertext),
    aad,
    fromBase64(packet.nonce),
    key,
  );
}

async function derivePinKey(pin: string, salt: Uint8Array, opsLimit: number, memLimit: number): Promise<Uint8Array> {
  await sodium.ready;
  return sodium.crypto_pwhash(
    sodium.crypto_aead_xchacha20poly1305_ietf_KEYBYTES,
    pin,
    salt,
    opsLimit,
    memLimit,
    sodium.crypto_pwhash_ALG_ARGON2ID13,
  );
}

async function deriveAccountWalletKey(
  accountPassword: string,
  pin: string,
  salt: Uint8Array,
  opsLimit: number,
  memLimit: number,
): Promise<Uint8Array> {
  await sodium.ready;
  if (!accountPassword) throw new Error('Enter the account password.');
  if (!/^\d{6,12}$/.test(pin)) throw new Error('Use a 6 to 12 digit Safe Wallet PIN.');
  return sodium.crypto_pwhash(
    sodium.crypto_aead_xchacha20poly1305_ietf_KEYBYTES,
    `${accountPassword}\u0000${pin}`,
    salt,
    opsLimit,
    memLimit,
    sodium.crypto_pwhash_ALG_ARGON2ID13,
  );
}

async function combinePinAndDeviceSecret(pinKey: Uint8Array, deviceSecret: Uint8Array): Promise<Uint8Array> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) throw new Error('This browser does not provide WebCrypto.');
  const imported = await subtle.importKey('raw', webCryptoBytes(pinKey), 'HKDF', false, ['deriveBits']);
  const bits = await subtle.deriveBits(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: webCryptoBytes(deviceSecret),
      info: webCryptoBytes(encoder.encode(DEVICE_WRAP_CONTEXT)),
    },
    imported,
    256,
  );
  return new Uint8Array(bits);
}

async function deriveHelloWrapKey(hardwareSecret: Uint8Array): Promise<Uint8Array> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) throw new Error('This browser does not provide WebCrypto.');
  const imported = await subtle.importKey('raw', webCryptoBytes(hardwareSecret), 'HKDF', false, ['deriveBits']);
  const bits = await subtle.deriveBits(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new Uint8Array(0),
      info: webCryptoBytes(encoder.encode(HELLO_DEVICE_SECRET_WRAP_CONTEXT)),
    },
    imported,
    256,
  );
  return new Uint8Array(bits);
}

/**
 * Wrap the device secret behind a hardware-held secret (for example a WebAuthn
 * PRF output that a platform authenticator releases only after a Windows Hello
 * check). On a Hello-enabled device the plaintext device secret is not stored,
 * so a copy of local storage cannot be used to brute-force the PIN offline
 * without also producing this hardware secret on the original device.
 */
export async function wrapDeviceSecretForHello(
  deviceSecret: Uint8Array,
  hardwareSecret: Uint8Array,
): Promise<CipherPacket> {
  await sodium.ready;
  const wrapKey = await deriveHelloWrapKey(hardwareSecret);
  try {
    return encryptPacket(deviceSecret, wrapKey, encoder.encode(HELLO_DEVICE_SECRET_WRAP_CONTEXT));
  } finally {
    sodium.memzero(wrapKey);
  }
}

export async function unwrapDeviceSecretForHello(
  wrapped: CipherPacket,
  hardwareSecret: Uint8Array,
): Promise<Uint8Array> {
  await sodium.ready;
  const wrapKey = await deriveHelloWrapKey(hardwareSecret);
  try {
    return decryptPacket(wrapped, wrapKey, encoder.encode(HELLO_DEVICE_SECRET_WRAP_CONTEXT));
  } catch {
    throw new Error('Could not unlock with fingerprint or face.');
  } finally {
    sodium.memzero(wrapKey);
  }
}

export async function createVaultRootKey(): Promise<Uint8Array> {
  await sodium.ready;
  return sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_KEYBYTES);
}

export async function createDeviceSecret(): Promise<Uint8Array> {
  await sodium.ready;
  return sodium.randombytes_buf(32);
}

export async function createDeviceAuthorizationToken(): Promise<string> {
  await sodium.ready;
  return toBase64(sodium.randombytes_buf(32));
}

export async function hashDeviceAuthorizationToken(token: string): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) throw new Error('This environment does not provide WebCrypto.');
  const digest = new Uint8Array(await subtle.digest('SHA-256', webCryptoBytes(encoder.encode(token))));
  return Array.from(digest, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function decodePairingKey(value: string, expectedBytes: number, label: string): Uint8Array {
  let decoded: Uint8Array;
  try {
    decoded = fromBase64(value);
  } catch {
    throw new Error(`Invalid ${label}.`);
  }
  if (decoded.byteLength !== expectedBytes) throw new Error(`Invalid ${label}.`);
  return decoded;
}

/**
 * Human-comparable fingerprint of the exact public key used for device pairing.
 * Both devices calculate it locally, so a backend that substitutes a key causes
 * a visible mismatch before the Vault Root Key is sealed.
 */
export async function deviceKeyFingerprint(publicKey: string): Promise<string> {
  await sodium.ready;
  const keyBytes = decodePairingKey(publicKey, sodium.crypto_box_PUBLICKEYBYTES, 'device pairing public key');
  const context = encoder.encode(DEVICE_FINGERPRINT_CONTEXT);
  const material = new Uint8Array(context.byteLength + keyBytes.byteLength);
  material.set(context, 0);
  material.set(keyBytes, context.byteLength);

  // libsodium's generic hash has a 16-byte minimum output. Keep 80 bits for a
  // compact, manually comparable fingerprint with additional margin over 64 bits.
  const digest = sodium.crypto_generichash(16, material, null).subarray(0, DEVICE_FINGERPRINT_BYTES);
  const hex = Array.from(digest, (byte) => byte.toString(16).padStart(2, '0')).join('').toUpperCase();
  return hex.match(/.{1,4}/g)?.join(' ') ?? hex;
}

export function encodeBytes(value: Uint8Array): string {
  return toBase64(value);
}

export async function decodeBytes(value: string): Promise<Uint8Array> {
  await sodium.ready;
  return fromBase64(value);
}

// Local PIN key-stretching cost (Argon2id time parameter). Memory stays at the
// INTERACTIVE tier (64 MiB) so low-end browsers never fail to allocate; only the
// time cost is raised, making offline PIN guessing more expensive. Raise further
// after benchmarking the lowest-end target device. Older envelopes keep their own
// stored cost and still open; they are re-wrapped at this cost on the next unlock.
export const PIN_KDF_OPSLIMIT = 4;

export function pinEnvelopeNeedsUpgrade(envelope: PinEnvelope): boolean {
  return envelope.version === 1
    && envelope.kdf.name === 'argon2id'
    && envelope.kdf.opsLimit < PIN_KDF_OPSLIMIT;
}

export async function createPinEnvelope(
  pin: string,
  deviceSecret: Uint8Array,
  vaultRootKey: Uint8Array,
): Promise<PinEnvelope> {
  if (!/^\d{6,12}$/.test(pin)) {
    throw new Error('Use a 6 to 12 digit app PIN.');
  }
  await sodium.ready;
  const salt = sodium.randombytes_buf(sodium.crypto_pwhash_SALTBYTES);
  const opsLimit = PIN_KDF_OPSLIMIT;
  const memLimit = sodium.crypto_pwhash_MEMLIMIT_INTERACTIVE;
  const pinKey = await derivePinKey(pin, salt, opsLimit, memLimit);
  const unlockKey = await combinePinAndDeviceSecret(pinKey, deviceSecret);
  try {
    return {
      version: 1,
      kdf: {
        name: 'argon2id',
        salt: toBase64(salt),
        opsLimit,
        memLimit,
      },
      wrappedVaultRootKey: encryptPacket(
        vaultRootKey,
        unlockKey,
        encoder.encode(DEVICE_WRAP_CONTEXT),
      ),
    };
  } finally {
    sodium.memzero(pinKey);
    sodium.memzero(unlockKey);
  }
}

export async function createAccountWalletKeyPackage(
  accountPassword: string,
  pin: string,
  vaultRootKey: Uint8Array,
  vaultId: string,
): Promise<AccountWalletKeyPackage> {
  await sodium.ready;
  const salt = sodium.randombytes_buf(sodium.crypto_pwhash_SALTBYTES);
  const opsLimit = sodium.crypto_pwhash_OPSLIMIT_INTERACTIVE;
  const memLimit = sodium.crypto_pwhash_MEMLIMIT_INTERACTIVE;
  const wrapKey = await deriveAccountWalletKey(accountPassword, pin, salt, opsLimit, memLimit);
  try {
    return {
      version: 1,
      kdf: {
        name: 'argon2id',
        salt: toBase64(salt),
        opsLimit,
        memLimit,
      },
      wrappedVaultRootKey: encryptPacket(
        vaultRootKey,
        wrapKey,
        associatedData([ACCOUNT_WALLET_WRAP_CONTEXT, vaultId]),
      ),
    };
  } finally {
    sodium.memzero(wrapKey);
  }
}

export async function openAccountWalletKeyPackage(
  accountPassword: string,
  pin: string,
  keyPackage: AccountWalletKeyPackage,
  vaultId: string,
): Promise<Uint8Array> {
  await sodium.ready;
  if (keyPackage.version !== 1 || keyPackage.kdf.name !== 'argon2id') {
    throw new Error('This wallet-key package is not supported by this app version.');
  }
  const wrapKey = await deriveAccountWalletKey(
    accountPassword,
    pin,
    fromBase64(keyPackage.kdf.salt),
    keyPackage.kdf.opsLimit,
    keyPackage.kdf.memLimit,
  );
  try {
    return decryptPacket(
      keyPackage.wrappedVaultRootKey,
      wrapKey,
      associatedData([ACCOUNT_WALLET_WRAP_CONTEXT, vaultId]),
    );
  } catch {
    throw new Error('The account password or Safe Wallet PIN is incorrect, or this wallet needs a one-time upgrade.');
  } finally {
    sodium.memzero(wrapKey);
  }
}

export async function unlockVaultRootKey(
  pin: string,
  deviceSecret: Uint8Array,
  envelope: PinEnvelope,
): Promise<Uint8Array> {
  await sodium.ready;
  const pinKey = await derivePinKey(
    pin,
    fromBase64(envelope.kdf.salt),
    envelope.kdf.opsLimit,
    envelope.kdf.memLimit,
  );
  const unlockKey = await combinePinAndDeviceSecret(pinKey, deviceSecret);
  try {
    return decryptPacket(
      envelope.wrappedVaultRootKey,
      unlockKey,
      encoder.encode(DEVICE_WRAP_CONTEXT),
    );
  } catch {
    throw new Error('The app PIN is incorrect or this device data is damaged.');
  } finally {
    sodium.memzero(pinKey);
    sodium.memzero(unlockKey);
  }
}

export async function encryptRecordPayload(
  payload: RecordPayload,
  vaultRootKey: Uint8Array,
  recordKind: RecordKind,
  revisionId: string,
): Promise<EncryptedRevision> {
  await sodium.ready;
  const recordKey = sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_KEYBYTES);
  const wrapAad = associatedData([RECORD_WRAP_CONTEXT, payload.recordId, revisionId, recordKind]);
  const payloadAad = associatedData([RECORD_PAYLOAD_CONTEXT, payload.recordId, revisionId, recordKind]);
  try {
    const wrapped = encryptPacket(recordKey, vaultRootKey, wrapAad);
    const encrypted = encryptPacket(encoder.encode(JSON.stringify(payload)), recordKey, payloadAad);
    return {
      cryptoVersion: 1,
      wrappedRecordKey: wrapped.ciphertext,
      wrappedRecordKeyNonce: wrapped.nonce,
      ciphertext: encrypted.ciphertext,
      nonce: encrypted.nonce,
    };
  } finally {
    sodium.memzero(recordKey);
  }
}

export async function decryptRecordPayload<T extends RecordPayload>(
  encrypted: EncryptedRevision,
  vaultRootKey: Uint8Array,
  recordId: string,
  recordKind: RecordKind,
  revisionId: string,
): Promise<T> {
  await sodium.ready;
  if (encrypted.cryptoVersion !== 1) throw new Error('Unsupported encrypted record version.');
  const wrapAad = associatedData([RECORD_WRAP_CONTEXT, recordId, revisionId, recordKind]);
  const payloadAad = associatedData([RECORD_PAYLOAD_CONTEXT, recordId, revisionId, recordKind]);
  const recordKey = decryptPacket(
    { nonce: encrypted.wrappedRecordKeyNonce, ciphertext: encrypted.wrappedRecordKey },
    vaultRootKey,
    wrapAad,
  );
  try {
    const plaintext = decryptPacket(
      { nonce: encrypted.nonce, ciphertext: encrypted.ciphertext },
      recordKey,
      payloadAad,
    );
    const parsed = JSON.parse(decoder.decode(plaintext)) as T;
    if (parsed.recordId !== recordId) throw new Error('Encrypted record identity mismatch.');
    return parsed;
  } finally {
    sodium.memzero(recordKey);
  }
}

export async function createPairingKeyPair(): Promise<{ publicKey: string; privateKey: string }> {
  await sodium.ready;
  const pair = sodium.crypto_box_keypair();
  return { publicKey: toBase64(pair.publicKey), privateKey: toBase64(pair.privateKey) };
}

export async function sealVaultRootKeyForDevice(vaultRootKey: Uint8Array, publicKey: string): Promise<string> {
  await sodium.ready;
  return toBase64(sodium.crypto_box_seal(
    vaultRootKey,
    decodePairingKey(publicKey, sodium.crypto_box_PUBLICKEYBYTES, 'device pairing public key'),
  ));
}

export async function openSealedVaultRootKey(
  sealed: string,
  publicKey: string,
  privateKey: string,
): Promise<Uint8Array> {
  await sodium.ready;
  try {
    return sodium.crypto_box_seal_open(
      fromBase64(sealed),
      decodePairingKey(publicKey, sodium.crypto_box_PUBLICKEYBYTES, 'device pairing public key'),
      decodePairingKey(privateKey, sodium.crypto_box_SECRETKEYBYTES, 'device pairing private key'),
    );
  } catch {
    throw new Error('This device approval could not be opened.');
  }
}

export async function clearBytes(bytes: Uint8Array | null | undefined): Promise<void> {
  if (!bytes) return;
  await sodium.ready;
  sodium.memzero(bytes);
}
